import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { SectionTitle, TintBand, BandTitle, ArrowLink, FadeIn } from "@/components/Editorial";

const areas = [
  {
    name: "Assetti societari.",
    text: " La forma giuridica dell'impresa, la struttura di gruppo, i soci, i patti e le regole della compagine sociale nel tempo.",
    to: "/assetti-societari",
    id: "area-assetti",
  },
  {
    name: "Processi.",
    text: " Come funziona l'azienda, e cosa cambia quando entra l'intelligenza artificiale.",
    to: "/processi",
    id: "area-processi",
  },
  {
    name: "Finanza.",
    text: " Come si misura l'impresa e come si sostiene, in azienda o in una raccolta di capitale.",
    to: "/finanza",
    id: "area-finanza",
  },
  {
    name: "M&A.",
    text: " Cessione e acquisizione di aziende: valutazione, negoziazione, closing dell'operazione.",
    to: "/m-a",
    id: "area-ma",
  },
];

const highlights = [
  {
    title: "Quando il problema arriva, le opzioni sono già finite.",
    text: "Cinque situazioni diverse, nate dalla stessa causa: una struttura societaria rimasta ferma mentre l'azienda si muoveva.",
    to: "/quando-il-problema-arriva",
    id: "highlight-quando",
  },
  {
    title: "Metriche e statuto: la posizione da cui si negozia.",
    text: "Un investitore chiede le metriche che dimostrano che il problema è reale, e uno statuto pronto a reggere la trattativa. Arrivare senza l'uno o l'altro significa negoziare da una posizione più debole.",
    to: "/metriche-e-statuto",
    id: "highlight-metriche",
  },
  {
    title: "M&A: acquisire, vendere, valutare.",
    text: "Operazioni di cessione e acquisizione come advisor indipendente, dalla valutazione alla chiusura della trattativa.",
    to: "/m-a",
    id: "highlight-ma",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Alessandro Immobile · Aziendalista e dottore commercialista"
        description="Assetti societari, processi, finanza e M&A per imprese e startup. Dal 1996 su bilanci, finanza e assetti societari; dal 2019 startup, dalla costituzione alla raccolta di capitale."
      />
      <main data-testid="home-page" className="pb-16 md:pb-20">
        {/* Hero immersivo a colori naturali (opz. 3b) */}
        <section
          data-testid="home-hero"
          className="relative w-full min-h-[88vh] flex items-end overflow-hidden"
        >
          <img
            src="/portrait-hero.png"
            alt="Ritratto di Alessandro Immobile"
            loading="eager"
          className="absolute inset-0 h-full w-full object-cover object-[center_8%]"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-[#0d1526]/95 via-[#0d1526]/55 to-transparent md:from-[#0d1526]/90 md:via-[#0d1526]/35"
          />
          <div className="relative z-10 w-full">
            <div className="mx-auto max-w-6xl px-6 pb-10 md:pb-14">
              <FadeIn>
                <p className="mb-4 text-xs uppercase tracking-[0.24em] text-white/75 font-medium">
                  Assetti societari · Processi · Finanza · M&A
                </p>
              </FadeIn>
              <FadeIn delay={0.04}>
                <h1
                  data-testid="home-title"
                  className="text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-white leading-[1.02] drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)]"
                >
                  Alessandro Immobile
                </h1>
              </FadeIn>
              <FadeIn delay={0.08}>
                <p className="mt-6 max-w-2xl font-serif text-xl md:text-[1.7rem] italic leading-snug text-white/90 drop-shadow-[0_1px_12px_rgba(0,0,0,0.35)]">
                  Financial advisor per le aziende. Business angel per le startup. Lo stesso metodo, applicato a modelli di crescita diversi.
                </p>
              </FadeIn>
            </div>
          </div>

          {/* Indicatore "scorri": linea verticale sottile animata */}
          <div
            aria-hidden
            data-testid="home-hero-scroll"
            className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 md:block"
          >
            <div className="scroll-line-track" />
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-6 mt-12 md:mt-14">
          <FadeIn>
          <div className="rounded-xl bg-[#F4F6FA] p-6 md:p-7 mb-3.5">
            <p className="text-lg leading-relaxed text-gray-700">
              <b className="text-navy">Dal 1996</b> lavoro su bilanci, finanza, processi e assetti societari: in studio, per gruppi bancari e imprese industriali, poi con ruoli esecutivi e di governo in società, e su operazioni di <b className="text-navy">M&A</b>.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.05}>
          <div className="rounded-xl bg-[#F4F6FA] p-6 md:p-7 mb-3.5">
            <p className="text-lg leading-relaxed text-gray-700">
              <b className="text-navy">Dal 2019</b> il campo si è esteso alle startup: validazione del modello, costituzione e statuto in chiave innovativa, raccolta di capitale di rischio e di debito, valutazione per i round di finanziamento.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="rounded-xl bg-[#F4F6FA] p-6 md:p-7 mb-3.5">
            <p className="text-lg leading-relaxed text-gray-700">
              Sono fondatore e CEO di <b className="text-navy">Imment S.r.l.</b>, dedicata ai modelli di crescita e di raccolta di capitale per startup e PMI. Sono partner di <b className="text-navy">P3 Ventures</b>, fondo che investe nelle fasi più giovani delle startup, co-fondatore di <b className="text-navy">Business Angel Club</b>, associazione di business angel.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="flex flex-col md:flex-row gap-3.5 mb-8">
            <div className="rounded-xl bg-[#F4F6FA] p-5 md:p-6 flex-1">
              <p className="text-base leading-relaxed text-gray-700">
                Docente di finanziamento aziendale alla <b className="text-navy">GIOYA Higher Education Institution</b>, a Malta.
              </p>
            </div>
            <div className="rounded-xl bg-[#F4F6FA] p-5 md:p-6 flex-1">
              <p className="text-base leading-relaxed text-gray-700">
                <b className="text-navy">Dottore commercialista</b>, iscritto all'Ordine di Torino, e <b className="text-navy">revisore legale</b>.
              </p>
            </div>
          </div>
        </FadeIn>

          <FadeIn delay={0.05}>
            <Link
              to="/referenze"
              data-testid="home-referenze-link"
              className="mt-3.5 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-6 py-5 no-underline hover:border-navy/30 transition-colors"
            >
              <span className="text-[15px] text-gray-700">Cosa dicono le persone con cui ho lavorato</span>
              <span className="text-sm font-semibold text-navy whitespace-nowrap">Leggi le referenze →</span>
            </Link>
          </FadeIn>

          <SectionTitle>Le quattro aree</SectionTitle>
          <p className="-mt-6 mb-8 text-xs tracking-[0.15em] text-gray-400 uppercase">Per aziende e startup</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {areas.map((a, i) => (
              <FadeIn key={a.id} delay={i * 0.05}>
                <div
                  data-testid={a.id}
                  className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-6 md:p-7"
                >
                  <p className="text-xs tracking-[0.15em] text-gray-400 mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif text-2xl text-navy mb-3">{a.name.replace(/\.$/, "")}</p>
                  <p className="text-base leading-relaxed text-gray-700">{a.text.trim()}</p>
                  <ArrowLink to={a.to} testId={`${a.id}-link`} className="mt-auto pt-5" labelClassName="text-[15px]">
                    Scopri di più
                  </ArrowLink>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <figure className="mt-12 md:mt-14 border-l-2 border-navy pl-6 md:pl-8">
              <blockquote className="font-serif text-[1.75rem] md:text-[2.15rem] italic leading-snug text-navy">
                Il lavoro che conta si fa prima che un investitore, una banca, un socio o una controparte facciano la prima domanda.
              </blockquote>
            </figure>
          </FadeIn>

          <TintBand testId="home-in-evidenza">
            <BandTitle>In evidenza</BandTitle>
            <div className="space-y-14">
              {highlights.map((h, i) => (
                <FadeIn key={h.id} delay={i * 0.05}>
                  <article data-testid={h.id}>
                    <h3 className="text-xl md:text-2xl font-medium tracking-tight text-gray-900 leading-snug">
                      {h.title}
                    </h3>
                    <p className="mt-4 text-lg leading-relaxed text-gray-700">{h.text}</p>
                    <ArrowLink to={h.to} testId={`${h.id}-link`} className="mt-5" labelClassName="text-[15px]">
                      Leggi
                    </ArrowLink>
                  </article>
                </FadeIn>
              ))}
            </div>
          </TintBand>
        </div>
      </main>
    </>
  );
}
